import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SlidePanel } from "./panelChrome";
import { Loader2, Plus, Trash2, Pencil, Clock, ChevronDown, LayoutTemplate } from "lucide-react";

const selCls = "w-full appearance-none rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 pr-8 text-sm text-slate-900 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none";
const dur = (a, b) => { const m = (t) => { const [h, mm] = t.split(":").map(Number); return h * 60 + mm; }; return Math.max(0, m(b) - m(a)); };

export default function ShiftPatternsManager({ workTypes = [], onClose, onChanged }) {
  const [items, setItems] = useState(null);
  const [canWrite, setCanWrite] = useState(false);
  const [editing, setEditing] = useState(null); // pattern obj or "new"

  const load = () => api.get("/scheduler/shift-patterns").then((r) => { setItems(r.data.items); setCanWrite(r.data.can_write); }).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!window.confirm(`Delete pattern "${p.name}"?`)) return;
    try { await api.delete(`/scheduler/shift-patterns/${p.id}`); toast.success("Deleted"); load(); onChanged?.(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <SlidePanel onClose={onClose} maxW="max-w-xl" testId="shift-patterns-manager">
      <div className="flex items-center justify-between border-b border-slate-100 pl-5 pr-14 py-4">
        <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2"><LayoutTemplate className="w-5 h-5 text-indigo-500" /> Shift Patterns</h3>
        {canWrite && <Button size="sm" className="gap-1 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setEditing("new")} data-testid="pattern-add"><Plus className="w-4 h-4" /> New Pattern</Button>}
      </div>
      <div className="flex-1 overflow-y-auto p-5">
        <p className="text-sm text-slate-500 mb-4">Reusable templates that pre-fill work type and start/end times when scheduling a shift.</p>
        {!items ? <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div> :
          items.length === 0 ? <div className="py-10 text-center text-sm text-slate-400">No patterns yet.</div> : (
            <div className="space-y-2">
              {items.map((p) => (
                <div key={p.id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3" data-testid={`pattern-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800">{p.name}</div>
                    <div className="text-xs text-slate-400 flex items-center gap-2">
                      {p.work_type && <span className="rounded-full bg-slate-100 px-2 py-0.5">{p.work_type}</span>}
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{p.start_time}–{p.end_time} · {p.duration_mins}m</span>
                    </div>
                  </div>
                  {canWrite && <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => setEditing(p)} className="text-slate-400 hover:text-indigo-600" data-testid={`pattern-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(p)} className="text-slate-400 hover:text-rose-600" data-testid={`pattern-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>}
                </div>
              ))}
            </div>
          )}
      </div>
      {editing && <PatternForm pattern={editing === "new" ? null : editing} workTypes={workTypes} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChanged?.(); }} />}
    </SlidePanel>
  );
}

function PatternForm({ pattern, workTypes, onClose, onSaved }) {
  const [name, setName] = useState(pattern?.name || "");
  const [workType, setWorkType] = useState(pattern?.work_type || "");
  const [start, setStart] = useState(pattern?.start_time || "09:00");
  const [end, setEnd] = useState(pattern?.end_time || "17:00");
  const [notes, setNotes] = useState(pattern?.notes || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("A name is required"); return; }
    setBusy(true);
    const payload = { name: name.trim(), work_type: workType || null, start_time: start, end_time: end, notes: notes || null };
    try {
      if (pattern) await api.put(`/scheduler/shift-patterns/${pattern.id}`, payload);
      else await api.post("/scheduler/shift-patterns", payload);
      toast.success("Pattern saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/40 flex justify-end" onClick={onClose} data-testid="pattern-form">
      <div className="bg-white h-full w-full sm:max-w-md shadow-2xl p-6 space-y-4 animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-slate-900">{pattern ? "Edit Pattern" : "New Pattern"}</h3>
        <div><label className="text-xs font-semibold text-slate-500">Name</label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" placeholder="e.g. Early Install" data-testid="pattern-name" /></div>
        <div><label className="text-xs font-semibold text-slate-500">Work Type</label>
          <div className="relative mt-1"><select value={workType} onChange={(e) => setWorkType(e.target.value)} className={selCls} data-testid="pattern-worktype"><option value="">Please select…</option>{workTypes.map((w) => <option key={w} value={w}>{w}</option>)}</select><ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /></div></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-semibold text-slate-500">Start</label><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" data-testid="pattern-start" /></div>
          <div><label className="text-xs font-semibold text-slate-500">End</label><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" data-testid="pattern-end" /></div>
        </div>
        <div className="text-xs text-slate-400">Duration: {dur(start, end)} minutes</div>
        <div><label className="text-xs font-semibold text-slate-500">Notes</label><Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" /></div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={save} data-testid="pattern-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save</Button>
        </div>
      </div>
    </div>
  );
}
